import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Box,
  Divider,
  Grid,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddCircleIcon from "@mui/icons-material/AddCircle";

import { useAxios } from "../../hooks";
import BadgeDetails from "./BadgeDetails";
import pages from "../../constants/pages";
import { convertDate } from "../../utils";
import { TextField } from "../../components";

const RecordCard = styled(Box)`
  padding: 16px;
  background: #ffffff;
  border: 1px solid #ddd;
  border-radius: 8px;
  transition: box-shadow 0.3s;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-bottom: 10px;
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }
`;

function CampusUser() {
  const axios = useAxios();
  const navigate = useNavigate();
  const { visitorData } = useSelector((state) => state.memberDetails);

  const [campusID, setCampusID] = useState("");
  const [userData, setUserData] = useState({});
  const [gatePassType, setGatePassType] = useState([]);
  const [badgeViewPopup, setBadgeViewPopup] = useState(false);
  const [parkingDetailsList, setParkingDetailsList] = useState([]);

  useEffect(() => {
    axios({
      url: "/attendance/campus/get-user-details?visitor_id=" + visitorData.ID,
    }).then((response) => {
      if (response.status) {
        setUserData(response.data);

        axios({
          url: `/admin/parking/user-parking-details?booking_id=${
            response.data?.student_id || response.data?.employee_id
          }`,
          disableRedirect: true,
          disableError: true,
        }).then((response) => {
          if (response.status) {
            setParkingDetailsList(response.data);
          }
        });
      }
    });

    axios({
      url: "/attendance/gate-pass/get-gate-pass-type",
    }).then((response) => {
      if (response.status) {
        setGatePassType(response.data);
      }
    });
  }, []);

  function handleGetGatePass(value) {
    navigate(
      pages.OutingPassListPage.route +
        "?type=" +
        value +
        "&campusID=" +
        userData?.premises_id
    );
  }

  function handleDeleteGatePass(ID) {
    axios({
      url: `attendance/gate-pass/delete-gate-pass?gate_pass_id=${ID}`,
      disableRedirect: true,
      disableError: true,
      method: "POST",
    }).then((response) => {
      if (response.status) {
        axios({
          url:
            "/attendance/campus/get-user-details?visitor_id=" + visitorData.ID,
        }).then((response) => {
          if (response.status) {
            setUserData(response.data);

            axios({
              url: `/admin/parking/user-parking-details?booking_id=${
                response.data?.student_id || response.data?.employee_id
              }`,
              disableRedirect: true,
              disableError: true,
            }).then((response) => {
              if (response.status) {
                setParkingDetailsList(response.data);
              }
            });
          }
        });
      }
    });
  }

  return (
    <Fragment>
      <Grid container justifyContent={"center"}>
        <Grid
          item
          container
          sx={{ "@media (min-width:900px)": { width: "50%" } }}
        >
          <Grid
            container
            spacing={3}
            sx={{ borderRadius: "10px", background: "white", padding: "10px" }}
          >
            <Grid
              item
              container
              sx={{
                border: "0.6px solid #0000001A",
                borderRadius: "10px",
                padding: "10px !important",
                backgroundColor: "#ffd0d0",
                backgroundImage:
                  "linear-gradient(164deg, rgb(232 232 255) 3%, rgb(255, 255, 255) 31%)",
                margin: "10px",
              }}
              spacing={2}
            >
              <Grid item container justifyContent="center" mb={1}>
                <Typography
                  fontSize="14px"
                  fontWeight="700"
                  color="#00000080"
                  sx={{ "-webkit-text-stroke-width": "0.5px" }}
                >
                  Student Pass
                </Typography>
              </Grid>

              {[
                ["ID", userData?.student_id],
                ["Campus Name", userData?.campus_name],
                ["Course", `${userData?.course} (${userData?.batch})`],
                ["Roll Number", userData?.roll_number],
                [
                  "Badge",
                  <Typography
                    variant="body2"
                    sx={{
                      background: userData?.badge_color,
                      borderRadius: "6px",
                      padding: "2px 10px",
                      fontSize: "12px",
                      display: "inline-block",
                    }}
                    onClick={() => {
                      setCampusID(userData?.campus_id);

                      setBadgeViewPopup(true);
                    }}
                  >
                    {userData?.badge_name}
                  </Typography>,
                ],
              ].map(([label, value], index) => (
                <Grid item container mt={index === 0 ? 0 : 1} key={label}>
                  <Grid item xs={5}>
                    <Typography fontSize="13px">{label}</Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <Typography fontSize="13px">:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    {typeof value === "string" ? (
                      <Typography fontSize="13px">{value}</Typography>
                    ) : (
                      value
                    )}
                  </Grid>
                </Grid>
              ))}

              <Grid item container mt={1}>
                <Grid item xs={6}>
                  <Typography fontSize="13px">Outing Pass</Typography>
                </Grid>
                <Grid
                  item
                  xs={6}
                  onClick={() => {
                    handleGetGatePass("outing");
                  }}
                >
                  <AddCircleIcon
                    sx={{ fontSize: "23px", color: "rgb(57, 54, 239)" }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {(parkingDetailsList?.length > 0 ||
            userData?.vehicle_numbers?.length > 0) && (
            <Grid
              item
              container
              sx={{
                border: "0.6px solid #0000001A",
                borderRadius: "16px",
                padding: "15px !important",
                backgroundColor: "#ffd0d0",
                backgroundImage:
                  "linear-gradient(164deg, rgb(232 232 255) 3%, rgb(255, 255, 255) 31%)",
                margin: "20px",
              }}
            >
              <Grid item container m={"0px 0px 15px"}>
                <Typography
                  variant="body1"
                  fontSize={"14px"}
                  fontWeight={"900"}
                  color={"#00000080"}
                >
                  Parking Vehicle
                </Typography>
              </Grid>

              {(parkingDetailsList.length > 0
                ? parkingDetailsList
                : userData.vehicle_numbers
              )?.map((item, index) => {
                return (
                  <Fragment>
                    {item?.vehicle_number ? (
                      <Grid item container>
                        <Typography variant="body1">
                          {`${index + 1} `}Vehicle
                        </Typography>
                        &ensp; : &nbsp;
                        <Typography variant="body1">
                          {item?.vehicle_number} {item?.vehicle_type}
                        </Typography>
                      </Grid>
                    ) : (
                      <Grid item container>
                        <Typography variant="body1">
                          {`${index + 1} `}Vehicle
                        </Typography>
                        &ensp; : &nbsp;
                        <Typography variant="body1">{item}</Typography>
                      </Grid>
                    )}
                  </Fragment>
                );
              })}
            </Grid>
          )}

          <Grid
            container
            spacing={2}
            sx={{ borderRadius: "10px", background: "white" }}
          >
            <Grid
              item
              container
              sx={{
                borderRadius: "16px",
                padding: "15px !important",
                margin: "20px 20px 0px 20px",
                justifyContent: "space-between",
              }}
            >
              <Grid item>
                <Typography
                  variant="body1"
                  fontSize={"18px"}
                  fontWeight={"900"}
                  color={"#00000080"}
                  sx={{ "-webkit-text-stroke-width": "1px" }}
                >
                  Outing Pass
                </Typography>
              </Grid>
              {/* <Grid item xs={6}>
            <TextField
              select
              size="small"
              label={"Select Get Gate Pass Type"}
              placeholder="Select Get Gate Pass Type"
              onChange={(e) => {
                handleGetGatePass(e.target.value);
              }}
            >
              {gatePassType.map((item, index) => {
                return (
                  <MenuItem key={index} value={item}>
                    {item}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid> */}
            </Grid>

            <Grid
              item
              container
              sx={{
                margin: "0px 20px 20px",
              }}
            >
              {userData?.gate_pass?.map((item, index) => {
                return (
                  <RecordCard style={{ position: "relative" }}>
                    <Tooltip title="Approve">
                      <DeleteIcon
                        sx={{
                          color: "red",
                          position: "absolute",
                          top: "-8px",
                          right: "-8px",
                          background: "white",
                          fontSize: "30px",
                          borderRadius: "10px",
                          padding: "3px",
                          border: "1.5px solid red",
                        }}
                        onClick={() => {
                          handleDeleteGatePass(item.gate_pass_id);
                        }}
                      />
                    </Tooltip>
                    <Grid
                      container
                      alignItems="center"
                      spacing={2}
                      onClick={() => {
                        navigate(
                          pages.OutingPage.route +
                            "?type=" +
                            item.gate_pass_type +
                            "&campusID=" +
                            userData?.premises_id +
                            "&passid=" +
                            item.gate_pass_config_id +
                            "&gatepassid=" +
                            item.gate_pass_id
                        );
                      }}
                    >
                      <Grid item container xs={12}>
                        <Grid item xs={8}>
                          <Typography variant="body1" fontWeight="900" mr={2}>
                            {item.gate_pass_type} Pass : {item.config_name}
                          </Typography>
                        </Grid>
                        <Grid item container xs={4}>
                          <Typography variant="body1" fontWeight="900" mr={2}>
                            {item.approval_status?.toUpperCase()}
                          </Typography>
                          {item.status == "pending" && (
                            <Tooltip title="Pending">
                              <ErrorOutlineIcon sx={{ color: "#ffd100" }} />
                            </Tooltip>
                          )}

                          {item.status == "approved" && (
                            <Tooltip title="Approve">
                              <CheckCircleOutlineIcon sx={{ color: "green" }} />
                            </Tooltip>
                          )}

                          {item.status == "rejected" && (
                            <Tooltip title="Rejected">
                              <ErrorOutlineIcon sx={{ color: "#ff0000ff" }} />
                            </Tooltip>
                          )}
                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="textSecondary"
                        >
                          {convertDate(item.from_time)}
                          {"  "} - {"  "}
                          {convertDate(item.till_time)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Divider />

                    <Grid
                      item
                      xs={12}
                      onClick={() => {
                        navigate(
                          pages.OutingPage.route +
                            "?type=" +
                            item.gate_pass_type +
                            "&campusID=" +
                            userData?.premises_id +
                            "&passid=" +
                            item.gate_pass_config_id +
                            "&gatepassid=" +
                            item.gate_pass_id
                        );
                      }}
                    ></Grid>

                    <Typography
                      variant="body2"
                      textAlign="left"
                      color="textSecondary"
                    >
                      Location: {item.visit_location}
                    </Typography>

                    <Typography
                      variant="body2"
                      textAlign="left"
                      color="textSecondary"
                    >
                      Purpose: {item.purpose}
                    </Typography>
                  </RecordCard>
                );
              })}
            </Grid>
          </Grid>

          <BadgeDetails
            campusID={campusID}
            badgeViewPopup={badgeViewPopup}
            setBadgeViewPopup={setBadgeViewPopup}
          />
        </Grid>
      </Grid>
    </Fragment>
  );
}

export default CampusUser;
